import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { cn } from "../lib/cn";

/**
 * Spinner — minimal loading indicator. Wraps lucide's Loader2.
 *
 * Three sizes; inherits `currentColor` so it picks up parent text color
 * automatically. Add `aria-label` if used in isolation (not paired with copy).
 */
const spinnerVariants = cva("animate-spin", {
  variants: {
    size: {
      sm: "h-3.5 w-3.5",
      default: "h-4 w-4",
      lg: "h-5 w-5",
      xl: "h-6 w-6",
    },
  },
  defaultVariants: {
    size: "default",
  },
});

export interface SpinnerProps
  extends Omit<React.ComponentProps<typeof Loader2>, "size">,
    VariantProps<typeof spinnerVariants> {
  /** When true, applies aria-hidden — for spinners next to a text label */
  decorative?: boolean;
}

export const Spinner = React.forwardRef<SVGSVGElement, SpinnerProps>(
  ({ className, size, decorative = false, "aria-label": ariaLabel = "Loading", ...props }, ref) => (
    <Loader2
      ref={ref}
      className={cn(spinnerVariants({ size }), className)}
      aria-label={decorative ? undefined : ariaLabel}
      aria-hidden={decorative ? true : undefined}
      role={decorative ? undefined : "status"}
      {...props}
    />
  ),
);
Spinner.displayName = "Spinner";
